import React, { useState, useEffect, useCallback } from "react";
import api from "../api/axios";
import { toast } from "sonner";
import { Phone, Clock, Star, X } from "lucide-react";

const CallScreen = ({ callData, onEnd }) => {
  const [callStatus, setCallStatus] = useState("Connecting");
  const [duration, setDuration] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");

  const startCall = useCallback(async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const response = await api.post(`/calls/${callData.callRequestId}/start`);
      setSessionId(response.data.session_id);
      setCallStatus("InCall");
      toast.success("Call connected!");
    } catch (error) {
      toast.error("Failed to start call");
      onEnd();
    }
  }, [callData.callRequestId, onEnd]);

  useEffect(() => {
    startCall();
  }, [startCall]);

  useEffect(() => {
    let interval;
    if (callStatus === "InCall") {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [callStatus]);

  const endCall = async () => {
    setShowRating(true);
  };

  const submitRating = async () => {
    try {
      await api.post(`/calls/${callData.callRequestId}/end`, {
        rating,
        feedback
      });
      toast.success("Thank you for your feedback!");
      onEnd();
    } catch (error) {
      toast.error("Failed to end call");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (showRating) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 max-w-md w-full" data-testid="rating-screen">
          <h2 className="text-2xl font-bold text-ocean font-outfit mb-6 text-center">Rate Your Experience</h2>
          
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= rating ? "fill-yellow-400 text-yellow-400" : "text-slate-300"
                  }`}
                />
              </button>
            ))}
          </div>

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Optional feedback..."
            className="w-full h-24 bg-slate-50 border border-slate-200 focus:border-aviation focus:ring-2 focus:ring-sky-100 rounded-xl p-4 resize-none mb-4"
            data-testid="feedback-input"
          />

          <button
            onClick={submitRating}
            disabled={rating === 0}
            className="w-full bg-aviation hover:bg-sky-600 text-white rounded-full h-12 font-semibold shadow-lg shadow-aviation/20 transition-transform active:scale-95 disabled:opacity-50"
            data-testid="submit-rating-button"
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-ocean to-ocean/90">
      <div className="text-center">
        <div className="w-32 h-32 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center mb-8 mx-auto animate-pulse">
          <Phone className="w-16 h-16 text-white" />
        </div>

        <h2 className="text-3xl font-bold text-white font-outfit mb-2" data-testid="call-status">
          {callStatus === "Connecting" ? "Connecting..." : "In Call"}
        </h2>

        {callData.mockMode && (
          <div className="bg-yellow-500/20 text-yellow-200 px-4 py-2 rounded-full text-sm mb-4 inline-block">
            MOCK MODE - Demo Call
          </div>
        )}

        {callStatus === "InCall" && (
          <div className="flex items-center justify-center gap-2 text-white/80 text-lg mb-8" data-testid="call-timer">
            <Clock className="w-5 h-5" />
            {formatTime(duration)}
          </div>
        )}

        <p className="text-white/60 mb-12">$2.00 per minute</p>

        {callStatus === "InCall" && (
          <button
            onClick={endCall}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full px-12 py-4 font-semibold text-lg shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 mx-auto"
            data-testid="end-call-button"
          >
            <X className="w-6 h-6" />
            End Call
          </button>
        )}
      </div>
    </div>
  );
};

export default CallScreen;
